import {
  type AnchorHTMLAttributes,
  type FC,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/router';
import styled from 'threads5/styles/styled';

// contexts
import { usePageLocale } from 'src/contexts';
import { useTrack } from 'src/contexts/AnalyticsContext';

// helpers
import { Events } from 'src/lib/trackingEvents';
import { prependLocale, prependStagingHash } from 'src/lib/utils';

// The anchor can sit anywhere inside its <LinkBox>; the ::after is what stretches
// to fill the nearest positioned ancestor (the LinkBox), making the whole box
// clickable while keeping a single real anchor in the DOM.
const StyledOverlayAnchor = styled('a')({
  // Global rules (e.g. `p a:not(.button)` via the hyperlink mixin) apply
  // `position: relative` to anchors. That would make the anchor the ::after's
  // containing block, trapping the overlay over just the link text. Force the
  // anchor static so the ::after stretches to the LinkBox instead. `&&` doubles
  // class specificity so this wins over those global selectors.
  '&&': {
    position: 'static',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 1,
  },
});

type LinkOverlayProps = {
  href: string;
  children: ReactNode;
  /** Optional analytics id forwarded on the BUTTON_CLICKED event payload. */
  trackingId?: string;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

/**
 * The single, real anchor in the link-overlay pattern (SITE-5558). Render it
 * inside a <LinkBox> and its ::after stretches across the box so the entire card
 * is clickable — without nesting <a> inside <a> (AEO/markdown-safe).
 *
 * Locale + staging-hash prefixing matches <Button>; there is no i18n routing in
 * next.config, so a plain locale-prefixed href is all that's needed.
 */
const LinkOverlay: FC<LinkOverlayProps> = ({
  href,
  children,
  trackingId = '',
  target,
  rel,
  onClick,
  ...rest
}) => {
  const router = useRouter();
  const userLocale = usePageLocale();
  const track = useTrack();
  const locale = router?.locale || userLocale;
  const basePath = router?.basePath || '';

  const resolvedHref = prependStagingHash(
    prependLocale({ locale, href }),
    basePath,
  );

  // Match the repo convention of pairing target="_blank" with a noopener rel.
  const resolvedRel =
    rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (trackingId) {
      track({
        type: Events.BUTTON_CLICKED,
        payload: { trackingId, event, currentTarget: event.currentTarget },
      });
    }
    onClick?.(event);
  };

  return (
    <StyledOverlayAnchor
      href={resolvedHref}
      target={target}
      rel={resolvedRel}
      onClick={handleClick}
      data-link-overlay=''
      {...rest}
    >
      {children}
    </StyledOverlayAnchor>
  );
};

export default LinkOverlay;
